import { CM_BUILTIN_EDGES, CM_STATUS_KEY_MAX } from '@bloo-team/api-client'
import type { CmCategory, CmPresets, CmStatus, CmStatusEdge } from '@bloo-team/api-client'

/**
 * Settings · Statuses & transitions, the pure part (docs/cases.md §Settings):
 * what a move between two statuses is, the moves out of and into a status,
 * the key a new status is saved under, and the category a closure preset
 * belongs to.
 */

/** `built-in` is fixed; `inherited` comes from the category's built-in; `allowed` / `blocked` are tenant rules. */
export type MoveKind = 'built-in' | 'inherited' | 'allowed' | 'blocked' | 'none'
export interface Move {
  status: CmStatus
  kind: MoveKind
}

const BUILTIN_PAIRS = new Set(CM_BUILTIN_EDGES.map(([a, b]) => `${a}>${b}`))

export function moveKind(from: CmStatus, to: CmStatus, edges: readonly CmStatusEdge[]): MoveKind {
  if (from.key === to.key) return 'none'
  if (from.builtin && to.builtin && BUILTIN_PAIRS.has(`${from.key}>${to.key}`)) return 'built-in'
  const rule = edges.find((e) => e.from === from.key && e.to === to.key)
  if (rule?.kind === 'block') return 'blocked'
  if (rule?.kind === 'allow') return 'allowed'
  return from.allowedNext.includes(to.key) ? 'inherited' : 'none'
}

export const canMove = (k: MoveKind) => k === 'built-in' || k === 'inherited' || k === 'allowed'

/** Where a case in `from` can go, where a rule stops it, and what is left to allow. Archived statuses take no moves in. */
export function movesFrom(from: CmStatus, statuses: readonly CmStatus[], edges: readonly CmStatusEdge[]) {
  const all = statuses.filter((t) => t.key !== from.key && !t.archived).map((t) => ({ status: t, kind: moveKind(from, t, edges) }))
  return {
    to: all.filter((m) => canMove(m.kind)),
    blocked: all.filter((m) => m.kind === 'blocked'),
    none: all.filter((m) => m.kind === 'none'),
  }
}

/** The live statuses a case can come from into `to`. */
export function movesInto(to: CmStatus, statuses: readonly CmStatus[], edges: readonly CmStatusEdge[]): Move[] {
  return statuses
    .filter((f) => f.key !== to.key && !f.archived)
    .map((f) => ({ status: f, kind: moveKind(f, to, edges) }))
    .filter((m) => canMove(m.kind))
}

/** The slug the key is made from: lowercase letters, digits, single underscores. */
export const slugFrom = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')

const RESERVED = new Set(['new', 'in_progress', 'on_hold', 'closed'])
const SLUG_MAX = CM_STATUS_KEY_MAX - 'c_'.length
const cut = (s: string, n: number) => s.slice(0, n).replace(/_+$/, '')

/**
 * The slug a new status is saved under (`c_<slug>`), never shown: from the
 * label, cut to fit the key's 30 characters, `status` when the label has no
 * latin letter or digit, and `_2`, `_3`… when the key is taken (archived
 * statuses included) or the slug is a built-in's.
 */
export function statusSlug(label: string, takenKeys: readonly string[]): string {
  const base = slugFrom(label) || 'status'
  const taken = new Set(takenKeys.map((k) => k.toLowerCase()))
  const free = (s: string) => !RESERVED.has(s) && !taken.has(`c_${s}`)
  let slug = cut(base, SLUG_MAX)
  for (let i = 2; !free(slug); i++) slug = `${cut(base, SLUG_MAX - String(i).length - 1)}_${i}`
  return slug
}

/** A preset's category is its target statuses'; with no target it has none (and nothing to change). */
export function presetCategory(p: CmPresets['presets'][number], statuses: readonly CmStatus[]): CmCategory | null {
  for (const key of p.targets) {
    const s = statuses.find((x) => x.key === key)
    if (s) return s.category
  }
  return null
}
